import type { ContentRef } from "@cogo/publisher-core";

/**
 * The single canonical opaque publication-ref contract. Studio transports exactly these sixteen
 * refs and nothing else: no browser credential, nonce, signature, PASS/verdict, `target`
 * discriminator, or application-name field may cross this boundary. No Studio-local alias,
 * redeclaration, parser, or second publication-ref type is permitted.
 */
export interface ApplicationOntologyPublicationRefs {
  profileRef: ContentRef;
  profileSelectionRef: ContentRef;
  projectScopeRef: ContentRef;
  actorScopeRef: ContentRef;
  applicationOntologyPackageRef: ContentRef;
  applicationPackageRef: ContentRef;
  compiledOntologyRef: ContentRef;
  compilerIdentityRef: ContentRef;
  requiredCapabilitySliceRef: ContentRef;
  requiredApplicationTopologySliceRef: ContentRef;
  requiredCapabilityCatalogRef: ContentRef;
  runtimeConsumerCatalogRef: ContentRef;
  artifactRef: ContentRef;
  publicationEligibilityRef: ContentRef;
  receiptChainHeadRef: ContentRef;
  evidenceRealmRef: ContentRef;
}

export interface ApplicationOntologyPublicationRequest {
  releaseId: string;
  refs: ApplicationOntologyPublicationRefs;
  applicationPublicationAuthorizationRef: ContentRef;
}

export const APPLICATION_ONTOLOGY_PUBLICATION_REF_KEYS = Object.freeze([
  "profileRef",
  "profileSelectionRef",
  "projectScopeRef",
  "actorScopeRef",
  "applicationOntologyPackageRef",
  "applicationPackageRef",
  "compiledOntologyRef",
  "compilerIdentityRef",
  "requiredCapabilitySliceRef",
  "requiredApplicationTopologySliceRef",
  "requiredCapabilityCatalogRef",
  "runtimeConsumerCatalogRef",
  "artifactRef",
  "publicationEligibilityRef",
  "receiptChainHeadRef",
  "evidenceRealmRef",
] as const);

const HASH_PATTERN = /^sha256:[a-f0-9]{64}$/;

function refusal(label: string): never {
  throw new Error(`APPLICATION_ONTOLOGY_PUBLICATION_REFS_INVALID:${label}`);
}

function parseRef(value: unknown, label: string): ContentRef {
  if (!value || typeof value !== "object" || Array.isArray(value)) refusal(label);
  const keys = Object.keys(value as Record<string, unknown>).sort();
  if (keys.length !== 2 || keys[0] !== "contentHash" || keys[1] !== "ref") refusal(label);
  const reference = value as { ref: unknown; contentHash: unknown };
  if (typeof reference.ref !== "string" || reference.ref.trim().length === 0) refusal(`${label}.ref`);
  if (typeof reference.contentHash !== "string" || !HASH_PATTERN.test(reference.contentHash)) {
    refusal(`${label}.contentHash`);
  }
  return { ref: reference.ref, contentHash: reference.contentHash as ContentRef["contentHash"] };
}

/**
 * Closed, dependency-neutral parser. Exactly the sixteen refs, each an exact `ContentRef`: any
 * missing key, any extra key, and any malformed ref is refused.
 */
export function parseApplicationOntologyPublicationRefs(input: unknown): ApplicationOntologyPublicationRefs {
  if (!input || typeof input !== "object" || Array.isArray(input)) refusal("refs");
  const record = input as Record<string, unknown>;
  const actual = Object.keys(record);
  if (actual.length !== APPLICATION_ONTOLOGY_PUBLICATION_REF_KEYS.length) refusal("keys");
  for (const key of APPLICATION_ONTOLOGY_PUBLICATION_REF_KEYS) {
    if (!Object.hasOwn(record, key)) refusal("keys");
  }
  const parsed = {} as Record<string, ContentRef>;
  for (const key of APPLICATION_ONTOLOGY_PUBLICATION_REF_KEYS) {
    parsed[key] = parseRef(record[key], key);
  }
  return parsed as unknown as ApplicationOntologyPublicationRefs;
}
